import i18n from '../i18n';

export type OrganizationOption = {
  code: string;
  name: string;
};

const normalize = (value?: string) => String(value ?? '').trim().toLowerCase();
let cachedLanguage = '';
let cachedOptions: OrganizationOption[] = [];
let cachedCodeToDisplay = new Map<string, string>();
let cachedCodeToCanonical = new Map<string, string>();
let cachedNameToCode = new Map<string, string>();

const isOrganizationOption = (value: unknown): value is OrganizationOption => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.code === 'string' && typeof candidate.name === 'string';
};

const ensureCache = () => {
  const language = i18n.resolvedLanguage || i18n.language || 'ko';
  if (cachedLanguage === language && cachedOptions.length > 0) return;

  const localizedOptions = i18n.t('page.myAccount.organizationOptions', {
    returnObjects: true,
    defaultValue: [],
  });

  const options = Array.isArray(localizedOptions)
    ? localizedOptions.filter(isOrganizationOption)
    : [];

  cachedLanguage = language;
  cachedOptions = options;
  cachedCodeToDisplay = new Map<string, string>();
  cachedCodeToCanonical = new Map<string, string>();
  cachedNameToCode = new Map<string, string>();

  options.forEach((option) => {
    const codeKey = normalize(option.code);
    const nameKey = normalize(option.name);
    if (!cachedCodeToDisplay.has(codeKey)) cachedCodeToDisplay.set(codeKey, option.name);
    if (!cachedCodeToCanonical.has(codeKey)) cachedCodeToCanonical.set(codeKey, option.code);
    if (!cachedNameToCode.has(nameKey)) cachedNameToCode.set(nameKey, option.code);
  });
};

export const getOrganizationOptions = () => {
  ensureCache();
  return cachedOptions;
};

export const getOrganizationDisplayName = (value?: string) => {
  ensureCache();
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return cachedCodeToDisplay.get(normalize(raw)) || raw;
};

export const getOrganizationCode = (value?: string) => {
  ensureCache();
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const normalized = normalize(raw);
  if (cachedCodeToCanonical.has(normalized)) return cachedCodeToCanonical.get(normalized) || raw;
  return cachedNameToCode.get(normalized) || raw;
};

/** 기타(ETC) 선택 시 사용하는 코드 */
export const ORGANIZATION_ETC_CODE = 'ETC';

/** organization 값이 사전 정의된 목록에 있는지 */
export const isOrganizationInPredefinedList = (value?: string) => {
  ensureCache();
  const code = getOrganizationCode(value);
  if (!code) return false;
  return cachedOptions.some((opt) => normalize(opt.code) === normalize(code));
};
